import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button, FormField, PasswordInput, PasswordStrengthMeter } from 'components/common';
import authAxios from 'helpers/authAxios';
import { extractApiError } from 'helpers/apiError';
import { passwordPolicyErrors } from 'helpers/passwordPolicy';
import useQuery from 'hooks/useQuery';

const PasswordResetForm = ({ hasLabel }) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Devise names it reset_password_token; the mail puts it in ?token= and that
  // name is now fixed by every link already sitting in somebody's inbox. Held in
  // state because the effect below takes it back out of the address bar.
  const query = useQuery();
  const [token] = useState(() => query.get('token'));

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors }
  } = useForm();

  const password = watch('password', '');

  // The token is a credential in a URL: it reaches the Referer header of every
  // third-party request this page makes (fonts, CDNs) and stays in history.
  // Reading it into state above is the last thing that needs it in the address
  // bar.
  useEffect(() => {
    if (!token) return;
    window.history.replaceState({}, document.title, window.location.pathname);
  }, [token]);

  const validatePolicy = (value) => {
    const problems = passwordPolicyErrors(value);
    return problems.length === 0 || `Password ${problems.join(', ')}.`;
  };

  const validateConfirmation = (value) =>
    value === getValues().password || 'Password confirmation does not match.';

  const onSubmit = async ({ password: newPassword, password_confirmation }) => {
    setSubmitting(true);
    try {
      await authAxios.put('/passwords', {
        reset_password_token: token,
        password: newPassword,
        password_confirmation
      });

      toast.success('Password changed. Sign in with your new password.');
      navigate('/authentication/login');
    } catch (err) {
      toast.error(extractApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Landing here without a token — an old bookmark, a truncated link — used to
  // let someone fill the whole form and then fail with an opaque 422.
  if (!token) {
    return (
      <div className="tw-mt-3 tw-text-center">
        <p className="tw-text-sm tw-text-slate-600">
          This password reset link is incomplete or has expired.
        </p>
        <Link className="tw-text-sm" to="/authentication/forgot-password">
          Request a new link
        </Link>
      </div>
    );
  }

  return (
    <form
      className={classNames('tw-mt-3 tw-flex tw-flex-col tw-gap-3', { 'tw-text-left': hasLabel })}
      onSubmit={handleSubmit(onSubmit)}
    >
      <FormField label={hasLabel ? 'New Password' : undefined} error={errors.password?.message}>
        <PasswordInput
          placeholder={!hasLabel ? 'New Password' : ''}
          autoComplete="new-password"
          error={!!errors.password}
          {...register('password', {
            required: 'Password is required.',
            validate: validatePolicy
          })}
        />
        <PasswordStrengthMeter password={password} />
      </FormField>

      <FormField
        label={hasLabel ? 'Confirm Password' : undefined}
        error={errors.password_confirmation?.message}
      >
        <PasswordInput
          placeholder={!hasLabel ? 'Confirm Password' : ''}
          autoComplete="new-password"
          error={!!errors.password_confirmation}
          {...register('password_confirmation', {
            required: 'Please type the password again.',
            validate: validateConfirmation
          })}
        />
      </FormField>

      <Button type="submit" fullWidth disabled={submitting}>
        {submitting ? 'Setting password…' : 'Set password'}
      </Button>
    </form>
  );
};

PasswordResetForm.propTypes = {
  hasLabel: PropTypes.bool
};

export default PasswordResetForm;
